interface EnvironmentConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  stripePublishableKey?: string;
}

class EnvironmentValidator {
  private static instance: EnvironmentValidator;
  private config: EnvironmentConfig;
  private errors: string[] = [];
  private warnings: string[] = [];

  private constructor() {
    this.config = this.validateAndLoad();
  }

  static getInstance(): EnvironmentValidator {
    if (!EnvironmentValidator.instance) {
      EnvironmentValidator.instance = new EnvironmentValidator();
    }
    return EnvironmentValidator.instance;
  }

  private validateAndLoad(): EnvironmentConfig {
    let supabaseUrl: string | undefined;
    let supabaseAnonKey: string | undefined;
    let stripePublishableKey: string | undefined;
    let encryptionSecret: string | undefined;

    try {
      supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      stripePublishableKey = import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY;
      encryptionSecret = import.meta.env?.VITE_API_KEY_ENCRYPTION_SECRET;
    } catch (error) {
      console.warn('Environment variables not available during initialization:', error);
      // Fallback to empty strings for graceful degradation
      supabaseUrl = '';
      supabaseAnonKey = '';
      stripePublishableKey = undefined;
      encryptionSecret = undefined;
    }

    if (!supabaseUrl) {
      this.errors.push('VITE_SUPABASE_URL is not defined');
    } else if (!this.isValidUrl(supabaseUrl)) {
      this.errors.push('VITE_SUPABASE_URL is not a valid URL');
    }

    if (!supabaseAnonKey) {
      this.errors.push('VITE_SUPABASE_ANON_KEY is not defined');
    } else if (!this.isValidSupabaseKey(supabaseAnonKey)) {
      this.errors.push('VITE_SUPABASE_ANON_KEY appears to be invalid');
    }

    if (!encryptionSecret) {
      this.warnings.push('VITE_API_KEY_ENCRYPTION_SECRET is not defined - user API key storage will be disabled');
    } else if (encryptionSecret.length < 32) {
      this.errors.push('VITE_API_KEY_ENCRYPTION_SECRET must be at least 32 characters for security. Generate with: openssl rand -base64 32');
    }

    if (!stripePublishableKey) {
      this.warnings.push('VITE_STRIPE_PUBLISHABLE_KEY is not defined - payment features will be disabled');
    } else if (!stripePublishableKey.startsWith('pk_')) {
      this.warnings.push('VITE_STRIPE_PUBLISHABLE_KEY does not appear to be a valid Stripe publishable key');
    }

    if (this.errors.length > 0) {
      console.error('Environment configuration errors:', this.errors);
      // In development, show helpful error message instead of crashing
      if (import.meta.env.DEV) {
        console.error(`\n🚨 MISSING ENVIRONMENT VARIABLES:\n${this.errors.map(e => `  - ${e}`).join('\n')}\n\nPlease create a .env file with these variables.\nSee SETUP_GUIDE.md for instructions.\n`);
      }
      throw new Error(`Environment validation failed:\n${this.errors.join('\n')}\n\nPlease create a .env file with required variables.\nSee SETUP_GUIDE.md for setup instructions.`);
    }

    if (this.warnings.length > 0) {
      console.warn('Environment configuration warnings:', this.warnings);
    }

    return {
      supabaseUrl: supabaseUrl || '',
      supabaseAnonKey: supabaseAnonKey || '',
      stripePublishableKey,
    };
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private isValidSupabaseKey(key: string): boolean {
    return key.length > 100 && key.includes('.');
  }

  getConfig(): EnvironmentConfig {
    return this.config;
  }

  hasErrors(): boolean {
    return this.errors.length > 0;
  }

  hasWarnings(): boolean {
    return this.warnings.length > 0;
  }

  getErrors(): string[] {
    return [...this.errors];
  }

  getWarnings(): string[] {
    return [...this.warnings];
  }

  isStripeConfigured(): boolean {
    return !!this.config.stripePublishableKey &&
           this.config.stripePublishableKey.startsWith('pk_');
  }

  isProduction(): boolean {
    return this.config.stripePublishableKey?.startsWith('pk_live_') || false;
  }

  get VITE_SUPABASE_URL(): string {
    return this.config.supabaseUrl;
  }

  get VITE_SUPABASE_ANON_KEY(): string {
    return this.config.supabaseAnonKey;
  }

  get VITE_STRIPE_PUBLISHABLE_KEY(): string | undefined {
    return this.config.stripePublishableKey;
  }

  get VITE_GITHUB_CLIENT_ID(): string {
    try {
      return import.meta.env?.VITE_GITHUB_CLIENT_ID || '';
    } catch {
      return '';
    }
  }

  get VITE_JIRA_CLIENT_ID(): string {
    try {
      return import.meta.env?.VITE_JIRA_CLIENT_ID || '';
    } catch {
      return '';
    }
  }
}

// Export singleton instance with lazy initialization to avoid TDZ errors
let _env: EnvironmentValidator | null = null;
const getEnv = (): EnvironmentValidator => {
  if (!_env) {
    _env = EnvironmentValidator.getInstance();
  }
  return _env;
};

// Use Proxy to forward all property access and method calls to the instance
export const env = new Proxy({} as EnvironmentValidator, {
  get(_target, prop) {
    const instance = getEnv();
    const value = (instance as any)[prop];
    // If it's a function, bind it to the instance
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getEnv();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getEnv();
    return prop in instance;
  },
});

// Lazy getter for envConfig to avoid TDZ errors
let _envConfig: EnvironmentConfig | null = null;
export const envConfig = new Proxy({} as EnvironmentConfig, {
  get(_target, prop) {
    if (!_envConfig) {
      _envConfig = env.getConfig();
    }
    return (_envConfig as any)[prop];
  },
  set(_target, prop, value) {
    if (!_envConfig) {
      _envConfig = env.getConfig();
    }
    (_envConfig as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    if (!_envConfig) {
      _envConfig = env.getConfig();
    }
    return prop in _envConfig;
  },
});
